import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { CitySlug, ServiceSlug } from '@/types'
import type { EnquiryErrors, EnquiryField, EnquiryValues } from '@/lib/enquiry'

import Link from 'next/link'
import { useEffect, useId, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cities } from '@/constants/cities'
import { services } from '@/constants/services'
import { siteConfig } from '@/constants/site'

import {
    ArrowRight,
    Check,
    CircleAlert,
    LoaderCircle,
    Phone
} from 'lucide-react'

import {
    ENQUIRY_FIELDS,
    ENQUIRY_LIMITS,
    OTHER_CITY,
    QUANTITY_OPTIONS,
    emptyEnquiry,
    validateEnquiry,
    validateField
} from '@/lib/enquiry'

interface EnquiryFormProps {
    defaultService?: ServiceSlug
    defaultCity?: CitySlug
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    hint?: string
    error?: string
    children: ReactNode
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

type SubmitResult = {
    ok?: boolean
    message?: string
    errors?: EnquiryErrors
}

const FALLBACK_ERROR = `We could not send your requirement. Please try again, or call us on ${siteConfig.phone.display}.`

const currentPath = (asPath: string): string => asPath.split(/[?#]/)[0] || '/'

const Field: FC<FieldProps> = ({ id, label, optional = false, hint, error, children }): ReactNode => {
    return (
        <div>
            <Label htmlFor={id}>
                {label}
                {optional && <span className='font-normal text-muted-foreground'>(optional)</span>}
            </Label>

            {hint && (
                <p id={`${id}-hint`} className='mt-1 text-sm text-muted-foreground'>
                    {hint}
                </p>
            )}

            <div className='mt-2'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-2 flex items-start gap-1.5 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

export const EnquiryForm: FC<EnquiryFormProps> = ({ defaultService, defaultCity }): ReactNode => {
    const router = useRouter()
    const formId = useId()
    const formRef = useRef<HTMLFormElement>(null)
    const honeypotRef = useRef<HTMLInputElement>(null)
    const successRef = useRef<HTMLDivElement>(null)
    const mountedAt = useRef(0)

    const [values, setValues] = useState<EnquiryValues>(() => emptyEnquiry({ service: defaultService, city: defaultCity }))
    const [errors, setErrors] = useState<EnquiryErrors>({})
    const [touched, setTouched] = useState<Partial<Record<EnquiryField, boolean>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [firstName, setFirstName] = useState('')
    const [enhanced, setEnhanced] = useState(false)

    useEffect(() => {
        mountedAt.current = Date.now()
        setEnhanced(true)
    }, [])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const fieldId = (field: string): string => `${formId}-${field}`

    const describedBy = (field: EnquiryField, hint = false): string | undefined => {
        const ids = [hint ? `${fieldId(field)}-hint` : '', errors[field] ? `${fieldId(field)}-error` : ''].filter(Boolean)
        return ids.length > 0 ? ids.join(' ') : undefined
    }

    const update = <K extends EnquiryField>(field: K, value: EnquiryValues[K]): void => {
        const next = { ...values, [field]: value }
        setValues(next)

        if (touched[field] || errors[field]) {
            setErrors((current) => ({ ...current, [field]: validateField(field, next) }))
        }
    }

    const handleChange = (field: Exclude<EnquiryField, 'services'>) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        update(field, event.target.value)
    }

    const handleBlur = (field: EnquiryField) => (): void => {
        setTouched((current) => ({ ...current, [field]: true }))
        setErrors((current) => ({ ...current, [field]: validateField(field, values) }))
    }

    const toggleService = (slug: ServiceSlug, checked: boolean): void => {
        const next = checked
            ? [...values.services, slug]
            : values.services.filter((item) => item !== slug)

        setTouched((current) => ({ ...current, services: true }))
        setValues({ ...values, services: next })
        setErrors((current) => ({ ...current, services: validateField('services', { ...values, services: next }) }))
    }

    const focusField = (field: EnquiryField): void => {
        formRef.current?.querySelector<HTMLElement>(`[name="${field}"]`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const nextErrors = validateEnquiry(values)
        const firstInvalid = ENQUIRY_FIELDS.find((field) => nextErrors[field])

        setErrors(nextErrors)
        setTouched({ services: true, name: true, phone: true, email: true, company: true, message: true })

        if (firstInvalid) {
            focusField(firstInvalid)
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/enquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    website: honeypotRef.current?.value ?? '',
                    elapsedMs: Date.now() - mountedAt.current,
                    sourcePage: currentPath(router.asPath)
                })
            })

            const result = (await response.json().catch(() => null)) as SubmitResult | null

            if (response.ok && result?.ok) {
                setFirstName(values.name.trim().split(/\s+/)[0] ?? '')
                setStatus('success')
                return
            }

            if (result?.errors && Object.keys(result.errors).length > 0) {
                setErrors(result.errors)
            }

            setServerMessage(result?.message ?? FALLBACK_ERROR)
            setStatus('error')
        }
        catch {
            setServerMessage(FALLBACK_ERROR)
            setStatus('error')
        }
    }

    const reset = (): void => {
        setValues(emptyEnquiry({ service: defaultService, city: defaultCity }))
        setErrors({})
        setTouched({})
        setServerMessage('')
        setStatus('idle')
        mountedAt.current = Date.now()
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='rounded-xl focus:outline-none'
            >
                <span className='flex size-12 items-center justify-center rounded-full bg-success/12 text-success'>
                    <Check aria-hidden='true' className='size-6' strokeWidth={2.5} />
                </span>

                <h3 className='mt-6 text-title font-semibold'>
                    {firstName ? `Thank you, ${firstName}. We have your requirement.` : "Thank you. We have your requirement."}
                </h3>

                <p className='mt-3 max-w-md leading-relaxed text-muted-foreground'>
                    Our team will call you back to understand your needs and share the right configuration with a clear quote.
                </p>

                <p className='mt-6 text-sm text-muted-foreground'>
                    Need to talk sooner?{' '}
                    <a href={siteConfig.phone.href} className='font-medium whitespace-nowrap text-foreground underline underline-offset-4'>
                        Call {siteConfig.phone.display}
                    </a>
                </p>

                <Button variant='outline' className='mt-8' onClick={reset}>
                    Send another requirement
                </Button>
            </div>
        )
    }

    const isSubmitting = status === 'submitting'

    return (
        <form
            ref={formRef}
            method='post'
            action='/api/enquiry'
            noValidate={enhanced}
            onSubmit={handleSubmit}
            className='relative space-y-6'
        >
            <fieldset aria-describedby={errors.services ? `${fieldId('services')}-error` : undefined}>
                <legend className='text-sm font-medium'>
                    What do you need?{' '}
                    <span className='font-normal text-muted-foreground'>Choose all that apply.</span>
                </legend>

                <div className='mt-3 grid grid-cols-1 gap-2.5 min-[420px]:grid-cols-2'>
                    {services.map((service) => {
                        const checked = values.services.includes(service.slug)

                        return (
                            <label key={service.slug} className='relative block'>
                                <input
                                    type='checkbox'
                                    name='services'
                                    value={service.slug}
                                    checked={checked}
                                    onChange={(event) => toggleService(service.slug, event.target.checked)}
                                    aria-invalid={Boolean(errors.services)}
                                    className='peer absolute inset-0 z-10 size-full cursor-pointer appearance-none rounded-lg opacity-0'
                                />

                                <span
                                    className={cn(
                                        'flex min-h-12 items-center gap-3 rounded-lg border bg-card px-3.5 py-2.5 text-[0.95rem] font-medium transition-colors',
                                        'peer-hover:border-foreground/60 peer-checked:border-ink peer-checked:bg-ink peer-checked:text-ink-foreground peer-checked:[&_svg]:opacity-100',
                                        'peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring',
                                        errors.services ? 'border-destructive' : 'border-input'
                                    )}
                                >
                                    <span aria-hidden='true' className='flex size-[1.125rem] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] border-current'>
                                        <Check className='size-3 opacity-0' strokeWidth={3.5} />
                                    </span>

                                    {service.shortName}
                                </span>
                            </label>
                        )
                    })}
                </div>

                {errors.services && (
                    <p id={`${fieldId('services')}-error`} className='mt-2 flex items-start gap-1.5 text-sm text-destructive'>
                        <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                        {errors.services}
                    </p>
                )}
            </fieldset>

            <Field id={fieldId('name')} label='Full name' error={errors.name}>
                <Input
                    id={fieldId('name')}
                    name='name'
                    autoComplete='name'
                    required
                    maxLength={ENQUIRY_LIMITS.name}
                    value={values.name}
                    onChange={handleChange('name')}
                    onBlur={handleBlur('name')}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy('name')}
                />
            </Field>

            <Field id={fieldId('phone')} label='Phone number' error={errors.phone}>
                <Input
                    id={fieldId('phone')}
                    name='phone'
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel'
                    required
                    placeholder='98765 43210'
                    value={values.phone}
                    onChange={handleChange('phone')}
                    onBlur={handleBlur('phone')}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={describedBy('phone')}
                />
            </Field>

            <Field id={fieldId('email')} label='Work email' optional error={errors.email}>
                <Input
                    id={fieldId('email')}
                    name='email'
                    type='email'
                    inputMode='email'
                    autoComplete='email'
                    placeholder='name@company.com'
                    maxLength={ENQUIRY_LIMITS.email}
                    value={values.email}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                />
            </Field>

            <Field id={fieldId('company')} label='Company' optional error={errors.company}>
                <Input
                    id={fieldId('company')}
                    name='company'
                    autoComplete='organization'
                    maxLength={ENQUIRY_LIMITS.company}
                    value={values.company}
                    onChange={handleChange('company')}
                    onBlur={handleBlur('company')}
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={describedBy('company')}
                />
            </Field>

            <Field id={fieldId('city')} label='City' optional>
                <NativeSelect
                    id={fieldId('city')}
                    name='city'
                    autoComplete='address-level2'
                    value={values.city}
                    onChange={handleChange('city')}
                >
                    <NativeSelectOption value=''>Select your city</NativeSelectOption>

                    {cities.map((city) => (
                        <NativeSelectOption key={city.slug} value={city.slug}>
                            {city.name}
                        </NativeSelectOption>
                    ))}

                    <NativeSelectOption value={OTHER_CITY}>Somewhere else</NativeSelectOption>
                </NativeSelect>
            </Field>

            <Field id={fieldId('quantity')} label='How many devices?' optional>
                <NativeSelect
                    id={fieldId('quantity')}
                    name='quantity'
                    value={values.quantity}
                    onChange={handleChange('quantity')}
                >
                    <NativeSelectOption value=''>Select a range</NativeSelectOption>

                    {QUANTITY_OPTIONS.map((option) => (
                        <NativeSelectOption key={option} value={option}>
                            {option}
                        </NativeSelectOption>
                    ))}
                </NativeSelect>
            </Field>

            <Field
                id={fieldId('message')}
                label='Your requirement'
                optional
                hint='Configuration, rental period, start date or anything else we should know.'
                error={errors.message}
            >
                <Textarea
                    id={fieldId('message')}
                    name='message'
                    rows={4}
                    maxLength={ENQUIRY_LIMITS.message}
                    placeholder='For example: 25 laptops, Core i5 with 16 GB RAM, for 6 months from 1 December'
                    value={values.message}
                    onChange={handleChange('message')}
                    onBlur={handleBlur('message')}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={describedBy('message', true)}
                />
            </Field>

            <div aria-hidden='true' className='absolute -left-[10000px] top-0 size-px overflow-hidden'>
                <label htmlFor={fieldId('website')}>Leave this field empty</label>
                <input
                    ref={honeypotRef}
                    id={fieldId('website')}
                    type='text'
                    name='website'
                    tabIndex={-1}
                    autoComplete='off'
                    defaultValue=''
                />
            </div>

            <input type='hidden' name='sourcePage' value={currentPath(router.asPath)} />

            {status === 'error' && serverMessage && (
                <div role='alert' className='flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    <p>{serverMessage}</p>
                </div>
            )}

            <div className='flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                <Button type='submit' size='lg' disabled={isSubmitting} className='w-full sm:w-auto'>
                    {isSubmitting
                        ? (
                            <>
                                <LoaderCircle aria-hidden='true' className='animate-spin' />
                                Sending
                            </>
                        )
                        : (
                            <>
                                Send my requirement
                                <ArrowRight aria-hidden='true' />
                            </>
                        )}
                </Button>

                <a
                    href={siteConfig.phone.href}
                    className='inline-flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium text-foreground underline-offset-4 hover:underline sm:justify-start'
                >
                    <Phone aria-hidden='true' className='size-4' />
                    Or call {siteConfig.phone.display}
                </a>
            </div>

            <p className='text-xs leading-relaxed text-muted-foreground'>
                We use your details only to respond to this enquiry. Read our{' '}
                <Link href='/privacy-policy' className='underline underline-offset-2 hover:text-foreground'>
                    privacy policy
                </Link>
                .
            </p>
        </form>
    )
}
